import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Col, Input, InputGroup, InputGroupText, Label, Row, Table } from "reactstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { Link } from "react-router-dom";
import { BsTrash3 } from "react-icons/bs";
import IconButton from "../../../../components/Buttons/IconButton";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { useSettings } from "../../../../context/settings";
import { get } from "../../../../utils/helpers/api";
import { calculateSalary } from "../../utils/payroll";

/**
 * Attaches allowances / deductions defined in HR settings to an employee. Each
 * row starts from the benefit type defaults and can override the calculation
 * for this employee only, which is what makes per-employee packages possible
 * without polluting the shared settings list.
 */
function BenefitsEditor({ employeeData, setEmployeeData }) {
  const { t } = useTranslation();
  const { settings, datePickerFormat } = useSettings();
  const [benefitTypes, setBenefitTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const currency = settings?.sales?.base_currency || '';
  const benefits = useMemo(() => employeeData.benefits || [], [employeeData.benefits]);

  useEffect(() => {
    const loadBenefitTypes = async () => {
      setLoading(true);
      const data = await get('/hr/benefit-types?only_active=1&page_size=100');
      if (data?.success) {
        setBenefitTypes(data?.benefit_types?.data || []);
      }
      setLoading(false);
    };

    loadBenefitTypes();
  }, []);

  const breakdown = useMemo(
    () => calculateSalary(employeeData.salary_amount, benefits),
    [employeeData.salary_amount, benefits]
  );

  const setBenefits = (rows) => setEmployeeData({ ...employeeData, benefits: rows });

  const addBenefit = (benefitType) => {
    if (!benefitType) return;

    setBenefits([
      ...benefits,
      {
        benefit_type_id: benefitType.id,
        name: benefitType.name,
        type: benefitType.type,
        calculation_type: benefitType.calculation_type,
        value: benefitType.value,
        percentage_base: benefitType.percentage_base,
        effective_from: null,
        effective_to: null,
      },
    ]);
  };

  const updateBenefit = (index, changes) =>
    setBenefits(benefits.map((row, i) => (i === index ? { ...row, ...changes } : row)));

  const removeBenefit = (index) => setBenefits(benefits.filter((_, i) => i !== index));

  const options = benefitTypes
    .filter(benefitType => !benefits.some(row => row.benefit_type_id === benefitType.id))
    .map(benefitType => ({
      value: benefitType.id,
      label: `${benefitType.name}${benefitType.code ? ` (${benefitType.code})` : ''}`,
      objectData: benefitType,
    }));

  const amountOf = (index, type) => {
    const lines = type === 'allowance' ? breakdown.allowances : breakdown.deductions;
    const row = benefits[index];
    return lines.find(line => line.benefit_type_id === row?.benefit_type_id)?.amount ?? 0;
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
        <h5 className="fw-bold mb-0">{t('hr.benefits.title')}</h5>
        <Link to="/hr/settings" className="small">{t('hr.benefits.manageInSettings')}</Link>
      </div>
      <p className="text-muted small">{t('hr.benefits.hint')}</p>

      <Row className="mb-3">
        <Col md="6">
          <Label>{t('hr.benefits.addBenefit')}</Label>
          <Select
            value={null}
            onChange={(selected) => addBenefit(selected?.objectData)}
            options={options}
            styles={SelectCustomStyles}
            isLoading={loading}
            placeholder={t('hr.benefits.selectBenefit')}
            noOptionsMessage={() => t('hr.benefits.noBenefitTypes')}
          />
        </Col>
      </Row>

      {benefits.length > 0 && (
        <Table responsive className="align-middle">
          <thead className="table-light">
            <tr>
              <th>{t('common.name')}</th>
              <th style={{ width: 140 }}>{t('common.type')}</th>
              <th style={{ width: 150 }}>{t('hr.benefits.calculationType')}</th>
              <th style={{ width: 170 }}>{t('hr.benefits.value')}</th>
              <th style={{ width: 170 }}>{t('hr.benefits.effectiveFrom')}</th>
              <th style={{ width: 140 }} className="text-end">{t('common.amount')}</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {benefits.map((row, index) => (
              <tr key={row.benefit_type_id}>
                <td>{row.name}</td>
                <td>
                  <Badge color={row.type === 'allowance' ? 'success' : 'danger'}>
                    {row.type === 'allowance' ? t('hr.allowance') : t('hr.deduction')}
                  </Badge>
                </td>
                <td>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={row.calculation_type}
                    onChange={(e) => updateBenefit(index, { calculation_type: e.target.value })}
                  >
                    <option value="fixed">{t('hr.benefits.fixed')}</option>
                    <option value="percentage">{t('hr.benefits.percentage')}</option>
                  </Input>
                </td>
                <td>
                  <InputGroup size="sm">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.value ?? ''}
                      onChange={(e) => updateBenefit(index, { value: e.target.value })}
                    />
                    <InputGroupText>
                      {row.calculation_type === 'percentage' ? '%' : currency}
                    </InputGroupText>
                  </InputGroup>
                  {row.calculation_type === 'percentage' && (
                    <Input
                      type="select"
                      bsSize="sm"
                      className="mt-1"
                      value={row.percentage_base || 'basic_salary'}
                      onChange={(e) => updateBenefit(index, { percentage_base: e.target.value })}
                    >
                      <option value="basic_salary">{t('hr.benefits.basicSalary')}</option>
                      <option value="gross_salary">{t('hr.benefits.grossSalary')}</option>
                    </Input>
                  )}
                </td>
                <td>
                  <DatePicker
                    selected={row.effective_from}
                    onChange={(date) => updateBenefit(index, { effective_from: date })}
                    className="form-control form-control-sm"
                    dateFormat={datePickerFormat}
                    placeholderText={t('hr.benefits.always')}
                    isClearable
                  />
                </td>
                <td className={`text-end fw-semibold ${row.type === 'allowance' ? 'text-success' : 'text-danger'}`}>
                  {row.type === 'allowance' ? '+' : '-'}{amountOf(index, row.type).toFixed(2)}
                </td>
                <td>
                  <IconButton size="sm" outline color="danger" onClick={() => removeBenefit(index)}>
                    <BsTrash3 />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <div className="border rounded p-3 mt-3">
        <div className="d-flex justify-content-between small mb-2">
          <span className="text-muted">{t('hr.basicSalary')}</span>
          <span>{breakdown.basicSalary.toFixed(2)} {currency}</span>
        </div>
        <div className="d-flex justify-content-between small mb-2">
          <span className="text-muted">{t('hr.benefits.totalAllowances')}</span>
          <span className="text-success">+{breakdown.totalAllowances.toFixed(2)} {currency}</span>
        </div>
        <div className="d-flex justify-content-between small mb-2">
          <span className="text-muted">{t('hr.benefits.grossSalary')}</span>
          <span>{breakdown.grossSalary.toFixed(2)} {currency}</span>
        </div>
        <div className="d-flex justify-content-between small mb-2">
          <span className="text-muted">{t('hr.benefits.totalDeductions')}</span>
          <span className="text-danger">-{breakdown.totalDeductions.toFixed(2)} {currency}</span>
        </div>
        <hr className="my-2" />
        <div className="d-flex justify-content-between fw-bold">
          <span>{t('hr.benefits.netSalary')}</span>
          <span>{breakdown.netSalary.toFixed(2)} {currency}</span>
        </div>
      </div>
    </>
  );
}

export default BenefitsEditor;
